import EffortTypes from './EffortTypes';
import ImpactFactorTypes from './ImpactFactorTypes';

/**
 * Read {@link https://help.canny.io/en/articles/5046937-how-do-prioritization-factors-and-scoring-work| the docs} to understand how the formula works.
 */
export const calculateScores = (roadmapPosts, roadmap, factors) => {
  const scoreMap = {};

  const weightSum = factors.reduce((weightSum, factor) => {
    if (factor.type === 'percentage') {
      return weightSum;
    }
    return weightSum + factor.weight;
  }, 0);

  // create a map with the max value for each factor across roadmap posts.
  const maxFactorValueMap = roadmapPosts.reduce((maxCalculationFactorValueMap, roadmapPost) => {
    const maxValues = Object.fromEntries(
      Object.entries(roadmapPost.factorValues).map(([factorID, factorValue]) => {
        if (!(factorID in maxCalculationFactorValueMap)) {
          return [factorID, factorValue];
        }

        return [factorID, Math.max(factorValue, maxCalculationFactorValueMap[factorID])];
      })
    );

    return {
      ...maxCalculationFactorValueMap,
      ...maxValues,
    };
  }, {});

  const maxPostScore = roadmapPosts.reduce((maxPostScore, roadmapPost) => {
    return roadmapPost.post.score > maxPostScore ? roadmapPost.post.score : maxPostScore;
  }, 1);

  roadmapPosts.forEach((roadmapPost) => {
    // score is 0 if effort is not defined
    if (!roadmapPost.effort || weightSum === 0) {
      scoreMap[roadmapPost._id] = 0;
      return;
    }

    const percentageMultiplier = factors.reduce((percentageMultiplier, factor) => {
      if (factor.type !== 'percentage') {
        return percentageMultiplier;
      }
      return percentageMultiplier * (roadmapPost.factorValues[factor._id] / 100);
    }, 1);

    const totalImpact =
      factors.reduce((totalImpact, factor) => {
        if (factor.type === 'percentage') {
          return totalImpact;
        }

        // normalize calculation factors to [0-100]
        if (factor.type === ImpactFactorTypes.calculation.name) {
          const maxValue = maxFactorValueMap[factor._id];
          if (!roadmapPost.factorValues[factor._id]) {
            return totalImpact;
          }

          const impact = roadmapPost.factorValues[factor._id] / maxValue;
          return totalImpact + factor.weight * (impact * 100);
        }

        const factorType = ImpactFactorTypes[factor.type];
        return (
          totalImpact +
          factor.weight * factorType.normalize(roadmapPost.factorValues[factor._id], maxPostScore)
        );
      }, 0) / weightSum;

    const effortType = EffortTypes[roadmap.effortType];

    // 1000 * [0-1] * [0-100] / [1-100] = [0-100k]
    scoreMap[roadmapPost._id] =
      (1000 * percentageMultiplier * totalImpact) / effortType.normalize(roadmapPost.effort);
  });
  return scoreMap;
};
